import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Send, ArrowRightCircle, Trash2, Eye, Check, Loader2, Repeat, FileDown, Search, CheckSquare, Square } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fileViewUrl, pdfUrl } from "@/lib/files";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useTableTools } from "@/lib/useTableTools";
import { money } from "@/lib/format";
import { flagUrl } from "@/i18n/translations";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = {
  client_name: "", client_email: "", deceased_name: "", transport_code: "",
  road_distance_km: 0, service_codes: [], origin: "", destination: "", notes: "",
};

// ISO2 country code -> CDN flag image (renders on all platforms, incl. Windows).
const CountryFlag = ({ cc, name, ip }) => {
  if (!cc || cc.length !== 2) return null;
  return (
    <img src={flagUrl(cc.toLowerCase(), 20)} alt={cc} loading="lazy"
      className="h-3.5 w-5 rounded-[2px] object-cover shadow-sm ring-1 ring-black/5"
      title={`${name || cc}${ip ? " · " + ip : ""}`} />
  );
};

export default function Quotes() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [settings, setSettings] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const isAdmin = user?.role === "admin";
  const tools = useTableTools(rows, ["number", "client_name", "origin", "destination", "status"]);

  const bulkDelete = async () => {
    const ids = [...tools.selected];
    if (!ids.length || !window.confirm(`${t("common.delete")} ${ids.length}?`)) return;
    try { await api.post("/quotes/bulk-delete", { ids }); toast.success("✓"); tools.clear(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const sel = tools.selectedRows.length ? tools.selectedRows : tools.filtered;
    const header = ["number", "client_name", "origin", "destination", "billable_km", "total", "status"];
    const csv = [header.join(",")].concat(sel.map((r) => header.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","))).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "quotes.csv"; a.click();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/quotes"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [load]);

  const name = (obj) => obj?.name?.[locale] || obj?.name?.en || obj?.code;

  // live estimate
  useEffect(() => {
    if (!open || !form.transport_code) { setEstimate(null); return; }
    const id = setTimeout(async () => {
      setEstimating(true);
      try {
        const { data } = await api.post("/quotes/estimate", {
          transport_code: form.transport_code,
          road_distance_km: Number(form.road_distance_km || 0),
          service_codes: form.service_codes, locale,
        });
        setEstimate(data);
      } catch (e) { setEstimate(null); }
      finally { setEstimating(false); }
    }, 400);
    return () => clearTimeout(id);
  }, [open, form.transport_code, form.road_distance_km, form.service_codes, locale]);

  const toggleService = (code) =>
    setForm((f) => ({ ...f, service_codes: f.service_codes.includes(code) ? f.service_codes.filter((x) => x !== code) : [...f.service_codes, code] }));

  const create = async () => {
    try {
      await api.post("/quotes", {
        ...form,
        client_email: form.client_email || null,
        road_distance_km: Number(form.road_distance_km || 0),
      });
      toast.success("Preventivo creato");
      setOpen(false); setForm(empty); setEstimate(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (q, status) => {
    try { await api.patch(`/quotes/${q.id}/status`, { status }); toast.success("✓"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post("/orders", {
        quote_id: q.id, client_id: q.client_id, client_name: q.client_name,
        client_email: q.client_email, deceased_name: q.deceased_name,
        transport_code: q.transport_code, origin: q.origin, destination: q.destination,
        road_distance_km: q.road_distance_km, billable_km: q.billable_km, total: q.total,
      });
      toast.success(`Ordine ${data.number} creato`);
      navigate(`/orders/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (q) => {
    if (!window.confirm(`${t("common.delete")}?`)) return;
    try { await api.delete(`/quotes/${q.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.quotes")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("sub.quotes")}</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEstimate(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="quote-create-btn" className="bg-blue-900 hover:bg-blue-800"><Plus size={16} className="mr-1.5" /> {t("common.create")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{t("quote.new")}</DialogTitle></DialogHeader>
            <div className="grid lg:grid-cols-2 gap-6 py-2">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Cliente</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="quote-client-name" /></div>
                  <div className="space-y-1.5"><Label>Email cliente</Label><Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="quote-client-email" /></div>
                </div>
                <div className="space-y-1.5"><Label>Nome defunto</Label><Input value={form.deceased_name} onChange={(e) => setForm({ ...form, deceased_name: e.target.value })} data-testid="quote-deceased" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Origine</Label><Input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="quote-origin" /></div>
                  <div className="space-y-1.5"><Label>Destinazione</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="quote-destination" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Tipo trasporto</Label>
                    <Select value={form.transport_code} onValueChange={(v) => setForm({ ...form, transport_code: v })}>
                      <SelectTrigger data-testid="quote-transport"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{settings?.wizard_transport?.map((s) => <SelectItem key={s.code} value={s.code}>{name(s)}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Distanza stradale (km)</Label><Input type="number" value={form.road_distance_km} onChange={(e) => setForm({ ...form, road_distance_km: e.target.value })} data-testid="quote-distance" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Servizi opzionali</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {settings?.wizard_services?.map((sv) => {
                      const sel = form.service_codes.includes(sv.code);
                      return (
                        <button type="button" key={sv.code} onClick={() => toggleService(sv.code)} data-testid={`quote-service-${sv.code}`}
                          className={`flex items-center justify-between text-left px-3 py-2 rounded-lg border text-sm ${sel ? "border-blue-900 bg-blue-50/50" : "border-slate-200"}`}>
                          <span className="flex items-center gap-1.5">{sel && <Check size={13} className="text-blue-900" />}{name(sv)}</span>
                          <span className="text-slate-500">{money(sv.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" /></div>
              </div>

              <div className="lg:border-l lg:pl-6 border-slate-100">
                <h4 className="font-display text-lg font-semibold text-slate-900 mb-3">Calcolo prezzo</h4>
                {estimating ? <div className="h-40 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
                  : estimate ? <Breakdown b={estimate} /> : <div className="text-sm text-slate-400 py-10 text-center">Seleziona trasporto e distanza per vedere il calcolo.</div>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={create} disabled={!estimate} data-testid="quote-save-btn" className="bg-blue-900 hover:bg-blue-800">{t("common.create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={tools.query} onChange={(e) => tools.setQuery(e.target.value)} placeholder={t("common.search")} className="pl-9" data-testid="quotes-search" />
        </div>
        <Button variant="outline" size="sm" onClick={tools.selectAllVisible} data-testid="quotes-select-all"><CheckSquare size={15} className="mr-1.5" /> {t("common.selectAll")}</Button>
        <Button variant="outline" size="sm" onClick={tools.clear} data-testid="quotes-clear"><Square size={15} className="mr-1.5" /> {t("common.deselect")}</Button>
      </div>

      {tools.selected.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-blue-900 text-white rounded-xl px-4 py-2.5" data-testid="quotes-bulk-bar">
          <span className="text-sm font-medium">{tools.selected.size} {t("common.selected")}</span>
          <div className="h-5 w-px bg-white/20 mx-1" />
          <Button size="sm" variant="secondary" className="h-8" onClick={exportCsv} data-testid="quotes-bulk-export"><FileDown size={14} className="mr-1" /> {t("bulk.export")}</Button>
          {isAdmin && <Button size="sm" variant="destructive" className="h-8" onClick={bulkDelete} data-testid="quotes-bulk-delete"><Trash2 size={14} className="mr-1" /> {t("bulk.delete")}</Button>}
          <Button size="sm" variant="ghost" className="h-8 ml-auto text-white hover:bg-white/10" onClick={tools.clear}>{t("common.cancelSelection")}</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10"></TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.number")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.client")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.route")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("col.opKm")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.total")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.status")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500 text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">{t("common.loading")}</TableCell></TableRow>
              : tools.filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
              : tools.filtered.map((q) => (
                <TableRow key={q.id} data-testid="quote-row">
                  <TableCell><Checkbox checked={tools.selected.has(q.id)} onCheckedChange={() => tools.toggle(q.id)} data-testid={`quote-check-${q.id}`} /></TableCell>
                  <TableCell className="font-medium text-slate-800">{q.number}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {q.client_country && (
                        <span data-testid="quote-flag"><CountryFlag cc={q.client_country} name={q.client_country_name} ip={q.client_ip} /></span>
                      )}
                      {q.client_name || "—"}
                    </span>
                    {q.campaign && <span className="block text-[10px] text-amber-600 mt-0.5" data-testid="quote-campaign">📣 {q.campaign}</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{q.origin} → {q.destination}</TableCell>
                  <TableCell className="text-sm">{q.billable_km ? `${q.billable_km} km` : "—"}</TableCell>
                  <TableCell className="font-medium">{money(q.total, q.currency)}</TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Dettaglio prezzo" onClick={() => setViewing(q)} data-testid="quote-view-btn"><Eye size={15} /></Button>
                      <a href={pdfUrl(`quotes/${q.id}/pdf`)} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" title="Scarica PDF" data-testid="quote-pdf-btn"><FileDown size={15} /></Button></a>
                      <Button size="icon" variant="ghost" title="Invia" onClick={() => setStatus(q, "sent")} data-testid="quote-send-btn"><Send size={15} /></Button>
                      <Button size="icon" variant="ghost" title="Converti in ordine" onClick={() => convert(q)} data-testid="quote-convert-btn" className="text-emerald-600"><ArrowRightCircle size={16} /></Button>
                      {isAdmin && <Button size="icon" variant="ghost" onClick={() => remove(q)} className="text-red-600" data-testid="quote-delete-btn"><Trash2 size={15} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="quote-view-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("quote.priceDetail")} · {viewing?.number}</DialogTitle></DialogHeader>
          {viewing && <Breakdown b={viewing.pricing || {}} fallback={viewing} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Breakdown({ b, fallback }) {
  const cur = b.currency || fallback?.currency || "EUR";
  const tr = b.transport || {};
  const mult = b.return_multiplier ?? fallback?.return_multiplier;
  const road = b.road_distance_km ?? fallback?.road_distance_km ?? fallback?.distance_km;
  const billable = b.billable_km ?? fallback?.billable_km;
  return (
    <div className="space-y-2 text-sm" data-testid="quote-breakdown">
      <Line label="Distanza stradale" value={road != null ? `${road} km` : "—"} />
      <Line label="Moltiplicatore A/R" value={mult != null ? `× ${mult}` : "—"} icon={Repeat} />
      <Line label="Distanza operativa" value={billable != null ? `${billable} km` : "—"} strong />
      <div className="h-px bg-slate-100 my-1" />
      <Line label="Prezzo base" value={tr.base_price != null ? money(tr.base_price, cur) : "—"} />
      <Line label="Prezzo per km" value={tr.price_per_km != null ? `${money(tr.price_per_km, cur)}/km` : "—"} />
      <Line label={`Costo trasporto${tr.name ? ` (${tr.name})` : ""}`} value={money(b.transport_cost ?? tr.cost ?? fallback?.transport_cost, cur)} strong />
      <div className="h-px bg-slate-100 my-1" />
      {(b.services || []).map((s, i) => <Line key={i} label={s.name} value={money(s.price, cur)} sub />)}
      <Line label="Totale servizi opzionali" value={money(b.optional_services_total ?? fallback?.optional_services_total ?? 0, cur)} />
      <div className="h-px bg-slate-100 my-1" />
      <Line label="Imponibile" value={money(b.subtotal ?? fallback?.subtotal, cur)} />
      <Line label={`IVA ${b.vat_pct ?? fallback?.vat_pct ?? 0}%`} value={money(b.tax ?? fallback?.tax, cur)} muted />
      <div className="flex items-center justify-between bg-slate-900 text-white rounded-lg px-4 py-3 mt-2">
        <span className="text-xs uppercase tracking-wider text-slate-300">Totale</span>
        <span className="font-display text-2xl font-bold" data-testid="quote-breakdown-total">{money(b.total ?? fallback?.total, cur)}</span>
      </div>
    </div>
  );
}

function Line({ label, value, strong, muted, sub, icon: Icon }) {
  return (
    <div className={`flex items-center justify-between ${muted ? "text-slate-400" : sub ? "text-slate-500 pl-3" : "text-slate-700"}`}>
      <span className={`flex items-center gap-1.5 ${strong ? "font-semibold text-slate-900" : ""}`}>{Icon && <Icon size={13} />}{label}</span>
      <span className={strong ? "font-semibold text-slate-900" : ""}>{value}</span>
    </div>
  );
}
